import { api } from "../api/http.js";
import type { Board, BoardRow, ChatCard, ChatMessage, ChatState, Contact, LastSeen, LedgerEntry, MailItem, MailList, Neighbour, ProfileCard, ProfileOptions } from "./types.js";
import type { Loot } from "../units/types.js";

const q = (params: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
};

export const chatApi = {
  state: () => api<ChatState>("GET", "/api/chat"),
  history: (channel: string, before?: number) => api<{ messages: ChatMessage[]; next: number | null; unread: number }>("GET", `/api/chat/${channel}${q({ before })}`),
  send: (channel: string, text: string, opts: { replyTo?: string; card?: ChatCard } = {}) => api<{ message: ChatMessage }>("POST", `/api/chat/${channel}`, { text, ...opts }).then((d) => d.message),
  dm: (accountId: string, text: string, opts: { replyTo?: string; card?: ChatCard } = {}) => api<{ message: ChatMessage }>("POST", `/api/chat/dm/${accountId}`, { text, ...opts }).then((d) => d.message),
  markRead: (channel: string) => api<{ ok: true }>("POST", `/api/chat/${channel}/read`),
  report: (messageId: string, reason: string) => api<{ reportId: string }>("POST", `/api/chat/messages/${messageId}/report`, { reason }),
  block: (accountId: string) => api<{ ok: true }>("POST", `/api/chat/block/${accountId}`),
  unblock: (accountId: string) => api<{ ok: true }>("DELETE", `/api/chat/block/${accountId}`),
  adminDelete: (messageId: string) => api<{ ok: true }>("DELETE", `/api/admin/chat/messages/${messageId}`),
  adminMute: (accountId: string, minutes: number, reason: string) => api<{ until: number }>("POST", `/api/admin/chat/mute/${accountId}`, { minutes, reason }),
};

export const mailApi = {
  list: (opts: { kind?: string; trash?: boolean; before?: number } = {}) => api<MailList>("GET", `/api/mail${q({ kind: opts.kind, trash: opts.trash ? 1 : undefined, before: opts.before })}`),
  read: (id: string) => api<{ mail: MailItem }>("POST", `/api/mail/${id}/read`).then((d) => d.mail),
  claim: (id: string) => api<{ mail: MailItem; loot: Loot }>("POST", `/api/mail/${id}/claim`),
  claimAll: () => api<{ claimed: number; loot: Loot }>("POST", "/api/mail/claim-all"),
  readAll: () => api<{ marked: number }>("POST", "/api/mail/read-all"),
  remove: (id: string) => api<{ ok: true }>("DELETE", `/api/mail/${id}`),
  restore: (id: string) => api<{ mail: MailItem }>("POST", `/api/mail/${id}/restore`).then((d) => d.mail),
  pin: (id: string) => api<{ mail: MailItem }>("POST", `/api/mail/${id}/pin`).then((d) => d.mail),
  unpin: (id: string) => api<{ mail: MailItem }>("POST", `/api/mail/${id}/unpin`).then((d) => d.mail),
  deleteRead: () => api<{ removed: number }>("POST", "/api/mail/delete-read"),
};

export const profilesApi = {
  card: (accountId: string) => api<{ card: ProfileCard }>("GET", `/api/profiles/${accountId}`).then((d) => d.card),
  options: () => api<ProfileOptions>("GET", "/api/profiles/me/options"),
  update: (patch: { avatar?: string | null; frame?: string; title?: string; colour?: string; showcaseSlot?: number }) => api<{ card: ProfileCard }>("PUT", "/api/profiles/me", patch).then((d) => d.card),
};

export const boardsApi = {
  board: (board: string) => api<Board>("GET", `/api/leaderboards/${board}`),
  history: (board: string, week: string) => api<{ rows: BoardRow[] }>("GET", `/api/leaderboards/${board}/history/${week}`).then((d) => d.rows),
};

export const socialApi = {
  neighbours: () => api<{ neighbours: Neighbour[] }>("GET", "/api/social/neighbours").then((d) => d.neighbours),
  assist: (accountId: string) => api<{ rowId: string; finishesAt: number; helpers: number }>("POST", `/api/social/assist/${accountId}`),
  contacts: () => api<{ contacts: Contact[] }>("GET", "/api/social/contacts").then((d) => d.contacts),
  addContact: (accountId: string) => api<{ ok: true }>("POST", `/api/social/contacts/${accountId}`),
  removeContact: (accountId: string) => api<{ ok: true }>("DELETE", `/api/social/contacts/${accountId}`),
  presence: (ids: string[]) => api<{ presence: { accountId: string; online: boolean; lastSeen: LastSeen }[] }>("GET", `/api/social/presence?ids=${ids.join(",")}`).then((d) => d.presence),
  exportData: () => api<Record<string, unknown>>("GET", "/api/me/export"),
  deletion: () => api<{ deletion: { requestedAt: number; dueAt: number } | null }>("GET", "/api/me/deletion").then((d) => d.deletion),
  requestDeletion: () => api<{ deletion: { requestedAt: number; dueAt: number } | null }>("POST", "/api/me/delete").then((d) => d.deletion),
  cancelDeletion: () => api<{ deletion: null }>("POST", "/api/me/delete/cancel"),
  ledger: (kind: "credits" | "resources" | "items" | "vip", cursor?: string) => api<{ rows: LedgerEntry[]; next: string | null }>("GET", `/api/ledger/${kind}${q({ cursor, limit: 50 })}`),
};
